<template>
  <div
    v-if="isOpen"
    class="legal-modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="legal-modal-title"
  >
    <div class="legal-modal-card">
      <div class="legal-modal-header">
        <h2 id="legal-modal-title">Atualização dos Termos</h2>
        <p>
          Há uma nova versão dos documentos legais. Leia e confirme para continuar
          usando a plataforma.
        </p>
      </div>

      <div v-if="loading" class="legal-modal-loading">Carregando documentos...</div>

      <div v-else class="legal-modal-body">
        <div
          v-for="doc in pending"
          :key="doc.doc_type + doc.version"
          class="legal-doc-block"
        >
          <div class="legal-doc-meta">
            <strong>{{ doc.title }}</strong>
            <span>Versão {{ doc.version }} · {{ formatDate(doc.published_at) }}</span>
          </div>
          <div class="legal-doc-content" v-html="doc.content_html"></div>
        </div>

        <label class="legal-check">
          <input v-model="accepted" type="checkbox" />
          <span>
            Li e concordo com
            <template v-if="hasTerms && hasPrivacy">os Termos de Uso e a Política de Privacidade</template>
            <template v-else-if="hasTerms">os Termos de Uso</template>
            <template v-else>a Política de Privacidade</template>
            atualizados.
          </span>
        </label>
      </div>

      <div class="legal-modal-actions">
        <button
          type="button"
          class="btn btn-primary legal-confirm"
          :disabled="!accepted || submitting || loading"
          @click="confirm"
        >
          {{ submitting ? "Salvando..." : "Confirmar e continuar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LegalHttp from "@/http/LegalHttp.js";

export default {
  name: "LegalAcceptanceModal",
  props: {
    isOpen: { type: Boolean, default: false },
    pending: { type: Array, default: () => [] },
  },
  data() {
    return {
      accepted: false,
      submitting: false,
      loading: false,
    };
  },
  computed: {
    hasTerms() {
      return this.pending.some((d) => d.doc_type === "terms");
    },
    hasPrivacy() {
      return this.pending.some((d) => d.doc_type === "privacy");
    },
  },
  watch: {
    isOpen(val) {
      if (val) this.accepted = false;
    },
  },
  methods: {
    formatDate(raw) {
      if (!raw) return "";
      const d = String(raw).slice(0, 10);
      const [y, m, day] = d.split("-");
      if (!y || !m || !day) return raw;
      return `${day}/${m}/${y}`;
    },
    async confirm() {
      if (!this.accepted || this.submitting) return;
      this.submitting = true;
      try {
        await LegalHttp.accept({
          source: "login",
          items: this.pending.map((d) => ({
            doc_type: d.doc_type,
            version: d.version,
          })),
        });
        this.$emit("accepted");
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.error ||
          "Não foi possível registrar o aceite. Tente novamente.";
        this.$toast?.add?.({
          severity: "error",
          summary: "Erro",
          detail: msg,
          life: 5000,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.legal-modal-card {
  width: min(720px, 100%);
  max-height: min(90vh, 860px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.legal-modal-header {
  padding: 20px 22px 10px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.legal-modal-header h2 {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a1230;
}

.legal-modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 0.92rem;
}

.legal-modal-body {
  padding: 14px 22px;
  overflow: auto;
  flex: 1;
}

.legal-modal-loading {
  padding: 40px;
  text-align: center;
  color: #64748b;
}

.legal-doc-block + .legal-doc-block {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.legal-doc-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;
}

.legal-doc-meta span {
  font-size: 0.8rem;
  color: #7c3aed;
  font-weight: 700;
}

.legal-doc-content {
  max-height: 220px;
  overflow: auto;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 0.88rem;
  line-height: 1.5;
  color: #334155;
}

.legal-doc-content :deep(h3) {
  font-size: 0.95rem;
  margin: 12px 0 6px;
  color: #1a1230;
}

.legal-doc-content :deep(p) {
  margin: 0 0 8px;
}

.legal-check {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 16px;
  font-size: 0.9rem;
  color: #1a1230;
  font-weight: 600;
  cursor: pointer;
}

.legal-check input {
  margin-top: 3px;
}

.legal-modal-actions {
  padding: 14px 22px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.legal-confirm {
  width: 100%;
  background: linear-gradient(135deg, #ff4ecd, #651ab0) !important;
  border: none !important;
  font-weight: 800;
}
</style>
